import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ApiError, api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Badge, Button, ErrorNote, PageLoader } from '@/components/ui'
import { useAsync } from '@/lib/useAsync'

export default function Join() {
  const { code = '' } = useParams()
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data, error: loadError, loading } = useAsync(() => api.previewInvite(code), [code])

  if (loading || authLoading) return <PageLoader />

  async function accept() {
    setJoining(true)
    setError(null)
    try {
      const res = await api.acceptInvite(code)
      navigate(`/campaigns/${res.campaignId}`, { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not join')
      setJoining(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm text-center">
        {loadError || !data ? (
          <div className="panel panel-lit p-6">
            <h1 className="font-display text-xl">Invite not found</h1>
            <p className="mt-2 text-sm text-ink-400">{loadError ?? 'That code does not match any campaign.'}</p>
            <Link to="/" className="mt-4 inline-block text-sm text-gold-400 underline">Go to your campaigns</Link>
          </div>
        ) : (
          <div className="panel panel-lit p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-ink-500">You have been invited to</p>
            <h1 className="mt-2 font-display text-2xl text-gold-400">{data.campaignName}</h1>
            <p className="mt-1.5 text-sm text-ink-400">
              Run by {data.dmName} · joining as <Badge tone={data.role === 'player' ? 'arcane' : 'neutral'}>{data.role}</Badge>
            </p>

            <div className="mt-6 space-y-3">
              {data.problem ? (
                <ErrorNote>{data.problem}</ErrorNote>
              ) : user ? (
                <>
                  {error && <ErrorNote>{error}</ErrorNote>}
                  <Button variant="primary" className="w-full" busy={joining} onClick={accept}>
                    Join as {data.dmName === user.displayName ? 'yourself' : user.displayName}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="primary" className="w-full" onClick={() => navigate(`/login?invite=${code}&next=/join/${code}`)}>
                    Create an account to join
                  </Button>
                  <p className="text-xs text-ink-500">
                    Already have one?{' '}
                    <Link to={`/login?next=/join/${code}`} className="text-gold-400 underline">Sign in</Link>
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
